/**
 * Dev-only cleanup: removes a service worker left behind by an earlier production-like build on the
 * same origin (e.g. `ng serve` of a config with `serviceWorker` set). Such a worker keeps
 * intercepting every dev-server request, including HMR `@ng/component` fetches, and makes the app
 * extremely slow. Reloads once if the page was being controlled by a worker that was removed.
 */
export interface StaleServiceWorkerDeps {
  serviceWorker?: ServiceWorkerContainer;
  cacheStorage?: CacheStorage;
  reload: () => void;
}

export async function unregisterStaleServiceWorkers(
  isProduction: boolean,
  deps: StaleServiceWorkerDeps = {
    serviceWorker: typeof navigator === 'undefined' ? undefined : navigator.serviceWorker,
    cacheStorage: typeof caches === 'undefined' ? undefined : caches,
    reload: () => globalThis.location.reload(),
  },
): Promise<void> {
  if (isProduction || !deps.serviceWorker) return;

  try {
    const registrations = await deps.serviceWorker.getRegistrations();
    if (registrations.length === 0) return;

    await Promise.all(registrations.map((r) => r.unregister()));
    if (deps.cacheStorage) {
      const keys = await deps.cacheStorage.keys();
      await Promise.all(keys.map((k) => deps.cacheStorage?.delete(k)));
    }
    if (deps.serviceWorker.controller) deps.reload();
  } catch {
    // Cleanup is best-effort; never block app start in dev.
  }
}
